import React from "react";

import { Box, DescriptionListTable, DescriptionListEntry, ColumnContainer } from "app/layout/components";
import { formatMoney, formatPercent } from "app/formatting/functions";

import "./style.scss";
import { PieChartWithContent } from "app/charts/PieChartWithContent/PieChartWithContent";

interface Props {
    buy: number;
    hold: number;
    sell: number;
    recommendation: string;
    targetValue: number;
    differencePercent: number;
    currency: string;
}

interface RecommondationPortion {
    label: string;
    percent: number;
    absolute: number;
}

const recommendationStrings: { [x: string]: string } = {
    buy: "kaufen",
    hold: "halten",
    sell: "verkaufen"
};

const calcHeight = (max: number, value: number): string => `${value / (max / 100) + 20}px`;
const calcPadding = (max: number, value: number): string => `${Math.min(80, 100 - value / (max / 100))}px`;

/**
 * Determines which recommendation is given overall.
 * If "buy" has the highest (or together with hold or sell same value), the recommendation is buy.
 * If "hold" has the highest (or together with sell same value), the recommendation is hold.
 * Otherwhise the recommendation is to sell.
 */
export const AnalysisOverview = ({
    buy,
    hold,
    sell,
    recommendation,
    targetValue,
    differencePercent,
    currency
}: Props) => {
    if (recommendationStrings[recommendation] === undefined) {
        recommendation = "buy";
    }

    const recommendationString = recommendationStrings[recommendation];
    const max = Math.max(buy, hold, sell);
    const total = buy + hold + sell;

    const percentBuy = buy / total;
    const percentHold = hold / total;
    const percentSell = sell / total;

    let results: RecommondationPortion[] = [
        {
            label: "Kaufen",
            percent: percentBuy,
            absolute: buy
        },
        {
            label: "Halten",
            percent: percentHold,
            absolute: hold
        },
        {
            label: "Verkaufen",
            percent: percentSell,
            absolute: sell
        }
    ];

    results = results.sort((a, b) => (a.absolute < b.absolute ? 1 : b.absolute < a.absolute ? -1 : 0));

    return (
        <Box alternateBackground className="analysisOverview">
            <ColumnContainer>
                <div className="diagram">
                    <ColumnContainer>
                        <PieChartWithContent values={[results[0].percent, results[1].percent, results[2].percent]}>
                            <h3 className={recommendation}>{recommendationString}</h3>
                            empfohlen
                        </PieChartWithContent>
                        <dl className="legend">
                            <div className="hold">
                                <dt>{results[0].label}</dt>
                                <dd>{results[0].absolute}</dd>
                            </div>
                            <div className="sell">
                                <dt>{results[1].label}</dt>
                                <dd>{results[1].absolute}</dd>
                            </div>
                            <div className="buy">
                                <dt>{results[2].label}</dt>
                                <dd>{results[2].absolute}</dd>
                            </div>
                        </dl>
                    </ColumnContainer>
                </div>
                <div className="context">
                    <h3>
                        {max} / {total} Analysten empfehlen zu &quot;{recommendationString}&quot;
                    </h3>
                    <DescriptionListTable>
                        <DescriptionListEntry term="Prognose Kursziel" details={formatMoney(targetValue, currency)} />
                        <DescriptionListEntry
                            term="Differenz zum aktuellen Kurs"
                            details={formatPercent(differencePercent)}
                        />
                    </DescriptionListTable>
                </div>
            </ColumnContainer>
        </Box>
    );
};
